/**
 * Which of a surface's two swap keyframes to apply, when the thing it is showing has been exchanged
 * for another one.
 *
 * ── Why there are TWO names for one animation ────────────────────────────────────────────────
 * A CSS animation replays only when its NAME changes. Stepping from phase 1 to phase 3 has to fade
 * exactly as stepping to phase 2 does, and stepping Monday → Tuesday → Monday has to fade twice, so
 * a single class applied on every change would animate once and then sit there. The mock does this
 * with a parity counter over two identical keyframes (`this.state.tick % 2 ? 'swapA' : 'swapB'`),
 * and this is that counter, with the two rules that surround it kept in one place rather than
 * rewritten per surface.
 *
 * ── The two rules ────────────────────────────────────────────────────────────────────────────
 * · **Nothing on the first render.** The counter starts at zero and answers `null` until a swap has
 *   actually happened, which is what keeps a cold entry still: a screen loads without moving, and an
 *   entrance fade on a surface that was never swapped is movement the trainer did not cause.
 * · **Nothing under `prefers-reduced-motion`.** Taken as a PARAMETER rather than left to
 *   `tokens/base.css`'s media block: that block collapses the duration to 0.01ms, which is the right
 *   outcome reached by a race, and a caller that reads the preference anyway can simply not emit the
 *   class. The media block stays as the net under every surface that does not.
 *
 * ── One consumer, deliberately ───────────────────────────────────────────────────────────────
 * The WEEK canvas's phase switch (`pages/plan-builder/ui/WeekPane`), and nothing else. The day
 * panel used this too until 9 September 2026 and now runs its own step through Motion — see
 * `features/plan-day-programming/lib/useDayStepMotion` for why the two surfaces stopped sharing a
 * mechanism. This one stays as it is: its keyframes were timed on 8 September to end exactly with
 * the day columns' `flex-grow` settle, and that pairing was measured rather than guessed.
 *
 * ── Why the comparison runs during RENDER ────────────────────────────────────────────────────
 * React's own "adjusting state when a prop changes" pattern. The re-render happens before the
 * browser paints, so no frame is drawn with the stale value and nothing moves. An effect would paint
 * the new content first and fade it in afterwards — a jump followed by an animation, which is the
 * defect this hook exists to remove. A ref would be a write during render, which the React Compiler
 * refuses outright.
 */

import { useState } from 'react';

/**
 * @param key What is being shown. A change to it is a swap — a phase index, a day's slot token.
 * @param reducedMotion Whether the viewer asked for stillness. Required, so no caller forgets it.
 * @returns `'a'` or `'b'`, alternating; `null` before the first swap and under reduced motion.
 */
export function useSwapReplay(key: string | number, reducedMotion: boolean): 'a' | 'b' | null {
  const [swap, setSwap] = useState({ key, tick: 0 });
  if (swap.key !== key) setSwap({ key, tick: swap.tick + 1 });

  if (reducedMotion || swap.tick === 0) return null;
  return swap.tick % 2 === 1 ? 'a' : 'b';
}
